import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate, { format } from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { Button, TextField, Card, Grid, CardHeader, CardContent } from '@mui/material';
import _ from 'underscore';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import {
	SetFormState,
	GetAutocompleteFields,
	ToggleAttachFileIncidentTrackerDialog,
	DownloadAttachment,
	ToggleRemoveFileIncidentTrackerDialog
} from '../../../../actions/IncidentTrackerActions';
import IncidentTrackerComments from './components/IncidentTrackerComments';

const schema = {
	siteId: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	date: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	status: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	levelOne: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	levelTwo: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	levelThree: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	solutionType: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	reportDateYear: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	},
	reportDateWeek: {
		presence: { allowEmpty: false, message: 'Mandatory' }
	}
};

const useStyles = makeStyles(theme => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1)
		}
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%'
	},
	divider: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1)
	}
}));

const IncidentTrackerDialogForm = props => {
	const { className, tableId, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.incidentTrackerDemoReducer.formState);
	const incidentDetails = useSelector(state => state.incidentTrackerDemoReducer.incidentDetails);
	const dropdowns = useSelector(state => state.incidentTrackerDemoReducer.dropdowns);
	const autocompleteFields = useSelector(state => state.incidentTrackerDemoReducer.autocompleteFields);
	const comments = useSelector(state => state.incidentTrackerDemoReducer.comments);
	const userId = useSelector(state => state.session.user.id);
	const _hasError = field => !!(formState.touched[field] && formState.errors[field]);
	// const [closedDate, setStatusClosedDate] = React.useState('');
	const _handleChange = event => {
		if (event.target.name === 'siteId') {
			dispatch(GetAutocompleteFields(event.target.value));
		}

		if (event.target.name === 'status') {
			if (event.target.value === '10') {
				setFormState({
					...formState,
					values: {
						...formState.values,
						[event.target.name]: event.target.value,
						closedDate: moment().format('YYYY-MM-DD HH:mm:ss'),
						ticketClosedFlag: true
					}
				});
			} else {
				setFormState({
					...formState,
					values: {
						...formState.values,
						[event.target.name]: event.target.value,
						closedDate: null,
						ticketClosedFlag: false
					}
				});
			}
		} else {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value
				},
				touched: {
					...formState.touched,
					[event.target.name]: true
				}
			});
		}
	};

	const _handleChangeLevels = event => {
		if (event.target.name === 'levelOne') {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === event.target.value)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelTwo: '',
					levelThree: '',
					levelsTwo,
					levelsThree: [],
				},
			});
		} else if (event.target.name === 'levelTwo') {
			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === event.target.value)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelThree: '',
					levelsThree,
				},
			});
		} else if (event.target.name === 'levelThree') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value
				},
			});
		}
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value
			},
			touched: {
				...formState.touched,
				[name]: true
			}
		});
	};

	const _onAddAttachmentClick = () => {
		dispatch(ToggleAttachFileIncidentTrackerDialog(true));
	};

	const _onDownloadAttachmentClick = () => {
		dispatch(DownloadAttachment(userId, formState.values.id));
	};

	const _onRemoveAttachmentClick = () => {
		dispatch(ToggleRemoveFileIncidentTrackerDialog(true));
	};

	useEffect(() => {
		const newTouched = {};

		if (formState.values.submit) {
			_.each(_.keys(schema), item => {
				newTouched[item] = true;
			});
		}

		const errors = validate(formState.values, schema, {
			fullMessages: false
		});

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			}
		});
	}, [formState.values]);

	useMemo(() => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				subRegion: autocompleteFields.subRegion,
				city: autocompleteFields.city
			}
		});
	}, [autocompleteFields]);

	useMemo(() => {
		// Check if ticket closed
		if (incidentDetails.status === '10') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					ticketClosedFlag: true,
					disableModalSaveButton: true
				}
			});
		} else {
			setFormState({
				...formState,
				values: {
					...formState.values,
					ticketClosedFlag: false,
					disableModalSaveButton: false
				}
			});
		}
	}, [incidentDetails]);

	useMemo(() => {
		if (dropdowns && dropdowns.levelsRelation) {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === formState.values.levelOne)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === formState.values.levelTwo)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					levelsTwo,
					levelsThree
				}
			});
		}
	}, [dropdowns]);

	return (
		<div className={classes.fields}>
			<Grid container spacing={1}>
				<Grid item md={4} xs={4}>
					<Card>
						<CardHeader title="Site Information" />
						<CardContent>
							<Grid container spacing={1}>
								<Grid item md={2} xs={2}>
									<TextField
										id="siteId"
										name="siteId"
										label="Site Id"
										variant="outlined"
										error={_hasError('siteId')}
										fullWidth
										helperText={
											_hasError('siteId')
												? formState.errors.siteId[0]
												: null
										}
										value={formState.values.siteId || ''}
										onChange={_handleChange}
										disabled={formState.values.ticketClosedFlag}
									/>
								</Grid>
								<Grid item md={5} xs={5}>
									<TextField
										id="subRegion"
										name="subRegion"
										label="Sub Region"
										variant="outlined"
										error={_hasError('subRegion')}
										fullWidth
										helperText={
											_hasError('subRegion')
												? formState.errors.subRegion[0]
												: null
										}
										value={formState.values.subRegion || ''}
										onChange={_handleChange}
										disabled={formState.values.ticketClosedFlag}
									/>
								</Grid>
								<Grid item md={5} xs={5}>
									<TextField
										id="city"
										name="city"
										label="City"
										variant="outlined"
										error={_hasError('city')}
										fullWidth
										helperText={
											_hasError('city')
												? formState.errors.city[0]
												: null
										}
										value={formState.values.city || ''}
										onChange={_handleChange}
										disabled={formState.values.ticketClosedFlag}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid item md={8} xs={8}>
					<Card>
						<CardHeader title="Additional Information" />
						<CardContent>
							<Grid container spacing={1}>
								<Grid item md={3} xs={3}>
									<TextField
										id="incNumber"
										name="incNumber"
										label="Inc Number"
										variant="outlined"
										error={_hasError('incNumber')}
										fullWidth
										helperText={
											_hasError('incNumber')
												? formState.errors.incNumber[0]
												: null
										}
										value={formState.values.incNumber || ''}
										onChange={_handleChange}
										disabled={formState.values.ticketClosedFlag}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<DatePicker
										id="creationDate"
										name="creationDate"
										label="Ticket Creation Date"
										style={{ width: '100%' }}
										value={
											formState.values.creationDate || ''
										}
										format="DD-MM-YYYY"
										disabled
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<DatePicker
										id="closedDate"
										name="closedDate"
										label="Ticket Closed Date"
										style={{ width: '100%' }}
										value={formState.values.closedDate || ''}
										onChange={value => _handleDateChange(value, 'closedDate')}
										minutesStep={1}
										error={
											_hasError('closedDate')
												? formState.errors.closedDate[0]
												: null
										}
										helperText={
											_hasError('closedDate')
												? formState.errors.closedDate[0]
												: null
										}
										format="DD-MM-YYYY"
										disabled
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="cfnOwner"
										name="cfnOwner"
										hintText="RF Owner"
										defaultValue=""
										menuItems={
											(dropdowns && dropdowns.cfnOwner)
											|| []
										}
										value={formState.values.cfnOwner || ''}
										onChange={_handleChange}
										disabled
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={1}>
				<Grid item md={12} xs={12}>
					<Card>
						<CardHeader title="RF Status" />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item md={3} xs={3}>
									<SelectField
										id="levelOne"
										name="levelOne"
										hintText="Category"
										defaultValue=""
										menuItems={(dropdowns && dropdowns.levelOne) || []}
										value={formState.values.levelOne || ''}
										onChange={_handleChangeLevels}
										disabled={formState.values.ticketClosedFlag}
										error={
											_hasError('levelOne')
												? formState.errors.levelOne[0]
												: null
										}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="levelTwo"
										name="levelTwo"
										hintText="KPI Impacted"
										defaultValue=""
										menuItems={formState.values.levelsTwo || []}
										value={formState.values.levelTwo || ''}
										onChange={_handleChangeLevels}
										disabled={formState.values.ticketClosedFlag}
										error={
											_hasError('levelTwo')
												? formState.errors.levelTwo[0]
												: null
										}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="levelThree"
										name="levelThree"
										hintText="Owner Ship"
										defaultValue=""
										menuItems={formState.values.levelsThree || []}
										value={formState.values.levelThree || ''}
										onChange={_handleChangeLevels}
										disabled={formState.values.ticketClosedFlag}
										error={
											_hasError('levelThree')
												? formState.errors.levelThree[0]
												: null
										}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="solutionType"
										name="solutionType"
										hintText="Next Actions"
										defaultValue=""
										menuItems={(dropdowns && dropdowns.solutionType) || []}
										value={formState.values.solutionType || ''}
										onChange={_handleChange}
										disabled={formState.values.ticketClosedFlag}
										error={
											_hasError('solutionType')
												? formState.errors.solutionType[0]
												: null
										}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="status"
										name="status"
										hintText="Status"
										defaultValue=""
										menuItems={
											(dropdowns && dropdowns.status)
											|| []
										}
										value={formState.values.status || ''}
										onChange={_handleChange}
										error={
											_hasError('status')
												? formState.errors.status[0]
												: null
										}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<DatePicker
										id="date"
										name="date"
										label="Analysis Date"
										style={{ width: '100%' }}
										value={formState.values.date || ''}
										onChange={value => _handleDateChange(value, 'date')}
										minutesStep={1}
										error={
											_hasError('date')
												? formState.errors.date[0]
												: null
										}
										helperText={
											_hasError('date')
												? formState.errors.date[0]
												: null
										}
										format="DD-MM-YYYY"
										disabled={formState.values.ticketClosedFlag}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="incidentDuration"
										name="incidentDuration"
										label="Ticket Duration"
										variant="outlined"
										error={_hasError('incidentDuration')}
										fullWidth
										helperText={
											_hasError('incidentDuration')
												? formState.errors.incidentDuration[0]
												: null
										}
										value={formState.values.incidentDuration || ''}
										disabled
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="reportDateYear"
										name="reportDateYear"
										label="Report Year"
										variant="outlined"
										error={_hasError('reportDateYear')}
										fullWidth
										helperText={
											_hasError('reportDateYear')
												? formState.errors.reportDateYear[0]
												: null
										}
										value={formState.values.reportDateYear || ''}
										onChange={_handleChange}
										disabled={formState.values.ticketClosedFlag}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="reportDateWeek"
										name="reportDateWeek"
										label="Report Week"
										variant="outlined"
										error={_hasError('reportDateWeek')}
										fullWidth
										helperText={
											_hasError('reportDateWeek')
												? formState.errors.reportDateWeek[0]
												: null
										}
										value={formState.values.reportDateWeek || ''}
										onChange={_handleChange}
										disabled={formState.values.ticketClosedFlag}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={1}>
				<Grid
					item
					md={12}
					xs={12}
					hidden={!tableId}
				>
					<IncidentTrackerComments tableId={tableId} comments={comments} />
				</Grid>
			</Grid>
			{/* New row */}
			{/* <Grid container spacing={1}>
				<Grid item md={12} hidden={!tableId}>
					<Grid container spacing={2}>
						<Grid
							item
							md={2}
							xs={2}
							hidden={formState.values.hasAttachment}
						>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onAddAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
							>
								Attach File
							</Button>
						</Grid>
						<Grid
							item
							md={2}
							xs={2}
							hidden={!formState.values.hasAttachment}
						>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onRemoveAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
							>
								Delete File
							</Button>
						</Grid>
						<Grid item md={2} xs={2}>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onDownloadAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
								disabled={!formState.values.hasAttachment}
							>
								Download File
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid> */}
		</div>
	);
};

IncidentTrackerDialogForm.propTypes = {
	className: PropTypes.string,
	tableId: PropTypes.string
};

export default IncidentTrackerDialogForm;
